import React, { useState } from 'react';

const HistoryList = ({ history = [], language, currentModule, handleReviewSession, page = 0, setPage, itemsPerPage = 5, minScore = 75, isAdminPreview = false, lastReviewedId = null }) => {
    const ITEMS_PER_PAGE = itemsPerPage;

    const totalPages = Math.ceil(history.length / ITEMS_PER_PAGE);

    const paginatedHistory = history.slice(
        page * ITEMS_PER_PAGE,
        (page + 1) * ITEMS_PER_PAGE
    );

    if (history.length === 0) {
        return (
            <div className="flex flex-col gap-6 w-full animate-in slide-in-from-bottom-4 fade-in duration-700 delay-200 mt-8">
                <div className="text-center py-8 text-gray-500 text-sm">{language === 'fr' ? 'Aucun historique disponible' : 'No history available'}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 w-full animate-in slide-in-from-bottom-4 fade-in duration-700 delay-200 mt-8">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-black uppercase tracking-widest text-gray-500 dark:text-white/50 flex items-center gap-3">
                    {language === 'fr' ? 'Historique des sessions' : 'Session History'}
                </h3>
                {/* Pagination — flèche LEFT = vers les plus récents, flèche RIGHT = remonter le temps */}
                <div className="flex items-center gap-1 md:gap-2 bg-gray-50 dark:bg-white/5 rounded-lg md:rounded-xl px-1.5 md:px-2 py-1 transition-all duration-300 flex-shrink-0">
                    <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="p-0.5 md:p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                        <svg className="w-3 h-3 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="text-[8px] md:text-[10px] font-black text-gray-400 dark:text-white/40 min-w-[35px] md:min-w-[30px] text-center uppercase tracking-tighter">
                        {page + 1}/{totalPages}
                    </span>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= totalPages - 1}
                        className="p-0.5 md:p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                        <svg className="w-3 h-3 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-3">
                {paginatedHistory.map((item, idx) => {
                    const displayDate = item.date || item.dateEnd || item.dateStart;
                    const isLastReviewed = lastReviewedId != null && String(item.id) === String(lastReviewedId);
                    return (
                        <div
                            key={idx}
                            onClick={() => handleReviewSession(item.id, item.isVuExamen)}
                            className={`cursor-pointer bg-white dark:bg-[#121212] rounded-[1.5rem] p-5 border flex flex-col gap-4 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-all duration-200 group shadow-sm dark:shadow-none transform hover:scale-[1.02] hover:shadow-md dark:hover:shadow-lg ${
                                isLastReviewed
                                    ? 'border-blue-400 dark:border-blue-500/60 ring-2 ring-blue-400/30 dark:ring-blue-500/20'
                                    : 'border-gray-200 dark:border-white/5'
                            }`}
                        >
                            {/* Exam Title */}
                            {item.title && (
                                <div className="text-sm font-bold text-gray-900 dark:text-white">
                                    {(language === 'en' ? item.title?.en : item.title?.fr) || item.title?.fr || 'Examen'}
                                    {isAdminPreview && (
                                        <span className="ml-2 text-[10px] font-mono text-amber-500 opacity-70">#{item.id} {item.isVuExamen ? '[vu]' : ''} examId:{item.examId}</span>
                                    )}
                                </div>
                            )}

                            {/* Main Content Row */}
                            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 w-full">
                                {/* Info Block */}
                                <div className="flex items-center gap-6 w-full lg:w-auto">
                                {/* Mini Circle */}
                                <div className="relative w-12 h-12 flex-shrink-0">
                                    <svg className="w-full h-full transform -rotate-90">
                                        <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-100 dark:text-white/5" />
                                        <circle cx="24" cy="24" r="20" stroke={item.score >= minScore ? '#10b981' : (item.score >= minScore - 10 ? '#fb923c' : '#ef4444')} strokeWidth="4" fill="transparent" strokeDasharray="126" strokeDashoffset={126 - (126 * item.score) / 100} strokeLinecap="round" />
                                    </svg>
                                    <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-gray-900 dark:text-white">{item.score}%</div>
                                </div>

                                {/* Details */}
                                <div className="flex flex-wrap gap-8 lg:gap-16">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">{language === 'fr' ? 'Validé le :' : 'Validation :'}</span>
                                        <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">{displayDate ? new Date(displayDate).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">{language === 'fr' ? 'Temps :' : 'Time :'}</span>
                                        <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                                            {Math.floor(item.duration / 60)} min {String(item.duration % 60).padStart(2, '0')}
                                        </span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">{language === 'fr' ? 'Score de passage :' : 'Pass Score :'}</span>
                                        <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">{minScore}%</span>
                                    </div>
                                </div>
                            </div>

                                {/* Button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleReviewSession(item.id, item.isVuExamen);
                                    }}
                                    className="w-full lg:w-auto px-6 py-2 rounded-xl bg-gray-100 dark:bg-white/5 group-hover:bg-gray-200 dark:group-hover:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-900 dark:text-white text-[10px] font-black uppercase tracking-widest transition-all border border-gray-200 dark:border-white/5 group-hover:border-gray-300 dark:group-hover:border-white/20 hover:border-gray-300 dark:hover:border-white/20 whitespace-nowrap"
                                >
                                    {language === 'fr' ? 'Revoir' : 'Review'}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default HistoryList;
